import { AxiosInstance } from "axios";
import { GetSearchIndexesResponse, MigrationPreview, SearchIndexStream } from "./searchIndexTypes";

const baseUrl = "/api/search-indexes";

// Every mutation returns the affected stream's new state, so callers re-render
// from one round trip instead of re-listing.
interface StreamResponse {
    stream: SearchIndexStream
}

export async function listSearchIndexes(req: AxiosInstance): Promise<SearchIndexStream[]> {
    const resp = await req.get<GetSearchIndexesResponse>(baseUrl);
    return resp.data?.streams || [];
}

// createSearchIndex creates the version this build targets and puts it into
// use. Startup does this by itself, so this is the retry for a deployment
// where that failed.
export async function createSearchIndex(req: AxiosInstance, stream: string): Promise<SearchIndexStream> {
    const resp = await req.post<StreamResponse>(`${baseUrl}/${stream}/indices`);
    return resp.data.stream;
}

// deleteSearchIndex destroys an index and its data. Pass confirm with the
// index name to delete the last copy that still holds the full history.
export async function deleteSearchIndex(
    req: AxiosInstance,
    stream: string,
    name: string,
    confirm?: string,
): Promise<SearchIndexStream> {
    const resp = await req.delete<StreamResponse>(
        `${baseUrl}/${stream}/indices/${encodeURIComponent(name)}`,
        confirm ? { params: { confirm } } : undefined,
    );
    return resp.data.stream;
}

// setSearchReadIndex points reads at one index — how an operator looks up
// records that stayed behind in a retired index. Reads never span two: while
// two indices are active they hold the same recent records under the same
// document ids, so a union would double every row.
export async function setSearchReadIndex(
    req: AxiosInstance,
    stream: string,
    index: string,
): Promise<SearchIndexStream> {
    const resp = await req.post<StreamResponse>(`${baseUrl}/${stream}/read`, { index });
    return resp.data.stream;
}

// getMigrationPreview reports what a copy would move and what it would leave
// behind, before anybody starts one. The destination defaults to the index
// currently in use.
export async function getMigrationPreview(
    req: AxiosInstance,
    stream: string,
    from: string,
    to?: string,
): Promise<MigrationPreview> {
    const resp = await req.get<{ preview: MigrationPreview }>(
        `${baseUrl}/${stream}/migration/preview`,
        { params: to ? { from, to } : { from } },
    );
    return resp.data.preview;
}

export async function startSearchIndexMigration(
    req: AxiosInstance,
    stream: string,
    from: string,
    to: string,
    requestsPerSecond?: number,
): Promise<SearchIndexStream> {
    const resp = await req.post<StreamResponse>(`${baseUrl}/${stream}/migration`, {
        from,
        to,
        requestsPerSecond,
    });
    return resp.data.stream;
}

// rethrottleSearchIndexMigration changes a running copy's rate without
// cancelling it. -1 removes the throttle.
export async function rethrottleSearchIndexMigration(
    req: AxiosInstance,
    stream: string,
    requestsPerSecond: number,
): Promise<SearchIndexStream> {
    const resp = await req.patch<StreamResponse>(`${baseUrl}/${stream}/migration`, { requestsPerSecond });
    return resp.data.stream;
}

export async function cancelSearchIndexMigration(
    req: AxiosInstance,
    stream: string,
): Promise<SearchIndexStream> {
    const resp = await req.delete<StreamResponse>(`${baseUrl}/${stream}/migration`);
    return resp.data.stream;
}
